import {Injectable} from "@angular/core";
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import {BehaviorSubject, Observable, throwError} from "rxjs";
import {catchError, filter, switchMap, take} from "rxjs/operators";
import {AuthService} from './auth.service';

/**
 * Interceptor to handle token-based authentication.
 * It checks if the request URL matches certain patterns, attaches the
 * bearer token, and refreshes it reactively on a 401 response.
 */
@Injectable({
		providedIn: 'root'
})
export class TokenInterceptorService implements HttpInterceptor {

	private isRefreshing = false;
	private readonly refreshedToken$ = new BehaviorSubject<string | null>(null);

	constructor(
		private readonly authService: AuthService
	) {}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const includeUrls = [
			'user/twitch/me',
			'auth/twitch/logout',
			'prediction/by-season',
			'prediction/create',
			'pixels/consume',
			'pixels/context',
			'statistic',
			'notifications',
			'boosters',
			'inventory',
			'stats',
			'marketplace',
			'cards',
			'battlepeps',
			'auth/discord',
			'user/discord',
			'/compare' // profile/{username}/compare uniquement — pas profile/{username} (public, doit rester sans token)
		];

		if (!includeUrls.some(url => request.url.includes(url))) {
			return next.handle(request);
		}

		const token = this.authService.getToken();
		const requestWithToken = token ? this.addToken(request, token) : request;

		return next.handle(requestWithToken).pipe(
			catchError(error => {
				if (error instanceof HttpErrorResponse && error.status === 401 && token) {
					return this.handleUnauthorized(request, next);
				}
				return throwError(() => error);
			})
		);
	}

	/**
	 * Refreshes the token on a 401 and retries the request.
	 * Concurrent 401s share a single in-flight refresh instead of each
	 * racing their own, which previously caused spurious logouts.
	 */
	private handleUnauthorized(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshedToken$.next(null);

			return this.authService.refreshToken().pipe(
				switchMap(newToken => {
					this.isRefreshing = false;
					this.refreshedToken$.next(newToken);
					return next.handle(this.addToken(request, newToken));
				}),
				catchError(error => {
					this.isRefreshing = false;
					return throwError(() => error);
				})
			);
		}

		return this.refreshedToken$.pipe(
			filter((token): token is string => token !== null),
			take(1),
			switchMap(token => next.handle(this.addToken(request, token)))
		);
	}

	private addToken(request: HttpRequest<any>, token: string) {
		return request.clone({
			setHeaders: {
				'Authorization': `Bearer ${token}`
			}
		});
	}
}
